import { Component, Input, Output, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { translate, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AddEvent, CancelEvent, EditEvent, GridComponent, GridDataResult, PageChangeEvent, RemoveEvent, RowClassArgs, SaveEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, debounceTime, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { BasketService } from 'src/app/basket/services/basket.service';
import { IBaseSearchRequest, IFilter, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { ICreateProductRequest, IProduct, IUpdateProductRequest } from 'src/app/products/models/product';
import { ProductService } from 'src/app/products/services/product.service';
import { ICreateBasketItemRequest, ICreateBasketRequest } from 'src/app/basket/models/basket';
import { ICompany } from 'src/app/company/models/company';
import { ProductInternalNumberValidator } from '../validators/product-internal-number';
import { ConnectivityStateService } from 'src/app/shared/services/connectivity-state.service';
import { ConfirmationDialogService } from '../services/confirmation-dialog.service';
import { Store, select } from '@ngrx/store';
import * as ProductsActions from "../../stores/global/products/actions";
import { isLoadingImportSelector, isLoadingSelector, productsSelector, productsTotalSelector } from 'src/app/stores/global/products/selectors';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';

@UntilDestroy()
@Component({
    selector: 'app-product-grid',
    templateUrl: './product-grid.component.html',
    styleUrls: ['./product-grid.component.scss'],
    standalone: false
})
export class ProductGridComponent implements OnInit {

  /////////////
  // Inputs //
  ///////////

  @Input() showActionCol : boolean = true;
  @Input() showOrderColumn : boolean = true;
  @Input() createProducts : boolean = true;
  @Input() reloadResults : boolean = true;
  @Input() loadAllProducts : boolean = true;
  @Input() isAdminView : boolean = false;
  @Input() currentCompany: ICompany = null;
  @ViewChild('productGrid') productGrid: GridComponent;


  ////////////////
  // Variables //
  //////////////

  // TODO: Could this not be replaced with this.request.keyword ?
  keyword : string;
  selectedFile: File | null = null;
  importForm: FormGroup;
  showUpload: boolean = false;
  products$: Observable<IProduct[]>;
  isInEditing = false;
  showReset = false;
  loggedInCompanyId: string;
  public deletionDialogOpened = false;
  public selectedItemToDelete: any = null;
  public deactivateDialogOpened = false;
  public selectedProductToDeactivate: IProduct = null;
  public inactiveReasonInput: string = '';

  public formGroup = new UntypedFormGroup({
    name: new UntypedFormControl(''),
    internalNumber: new UntypedFormControl('')
  });

  private editedRowIndex!: number | undefined;

  filterForm = this.fb.group({
    keyword: [null]
  });

  constructor(
    private route : ActivatedRoute,
    private productService: ProductService,
    private fb : UntypedFormBuilder,
    private appnotificationService: AppNotificationService,
    private basketService: BasketService,
    private connectivityStateService : ConnectivityStateService,
    private confirmationDialogService : ConfirmationDialogService,
    private store: Store<AppStateInterface>
  ) {

    this.importForm = this.fb.group({
      file: [null, Validators.required]
    });
  }

  ngOnInit(): void {}

  /////////////////////////
  // (Behavior)Subjects //
  ///////////////////////
  pageChangeSub$ = new Subject<PageChangeEvent>();
  createItemSub$ = new Subject<ICreateProductRequest>();
  updateItemSub$ = new Subject<IUpdateProductRequest>();
  deleteItemSub$ = new Subject<string>();
  gridState$ = new BehaviorSubject<IGridState>(
    {
      pageSize: PageSize.Small,
      skip: 0,
    }
  );

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small
  });
  
  public connectionState$ : Observable<boolean> = this.connectivityStateService.connectionState$;
  
  vm$ = combineLatest([
    this.store.pipe(select(currentEmployeeSelector)).pipe(tap((employee) => {
      if (employee?.company?.id) {
        this.loggedInCompanyId = employee.company.id;
      }
    })),
    this.store.pipe(select(productsSelector)),
    this.store.pipe(select(isLoadingSelector)),
    this.store.pipe(select(productsTotalSelector)),
    this.store.pipe(select(isLoadingImportSelector)),
    this.createItemSub$.pipe(
      tap(request => this.store.dispatch(ProductsActions.createProduct({product: request}))),
      startWith(null),
    ),
    this.updateItemSub$.pipe(
      tap(request => this.store.dispatch(ProductsActions.updateProduct({product: request}))),
      startWith(null),
    ),
    this.deleteItemSub$.pipe(
      tap(id => this.store.dispatch(ProductsActions.deleteProduct({id}))),
      startWith(null),
    ),
    this.gridState$,
    this.pageChangeSub$.pipe(
      withLatestFrom(this.searchRequest$),
      map(([pageChangeEvent, searchRequest]) => ({
        pageChangeEvent,
        searchRequest,
      })),
      tap((x) => {
        const pageNumber = Math.round(
          (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
            x.pageChangeEvent.take
        );
        this.gridState$.next({
          skip: x.pageChangeEvent.skip,
          pageSize: x.pageChangeEvent.take,
        });
        this.searchRequest$.next({
          ...x.searchRequest,
          pageNumber: pageNumber,
          pageSize: x.pageChangeEvent.take,
        });
      }),
      startWith({ skip: 0, take: 10 })
    ),
    this.searchRequest$.pipe(
      debounceTime(100),
      tap(request => this.store.dispatch(ProductsActions.getProducts({request})))
    )
  ]).pipe(
    map(
      ([
        currentEmployee,
        products,
        isLoadingProducts,
        total,
        isLoading,
        createItemSub,
        updateItemSub,
        deleteItemSub,
        gridState,
        pageChangeSub,
        searchRequest
      ]) => ({
        currentEmployee,
        products,
        isLoadingProducts,
        total,
        isLoading,
        createItemSub,
        updateItemSub,
        deleteItemSub,
        gridState,
        pageChangeSub,
        searchRequest
      })
    )
  );

  /////////////
  // getter //
  ///////////

  get pageSizes() {
    return PageSize;
  }

  public rowClass = (context: RowClassArgs) => {
    return { 'inactive-product': !context.dataItem.isActive };
  };


  ////////////////
  // functions //
  //////////////

  reloadProducts() {
    if (this.loadAllProducts || (this.keyword && this.keyword !== "")) {
      this.searchRequest$.next({ pageNumber: 0, pageSize: PageSize.Small });
      this.store.dispatch(ProductsActions.getProducts({ request: { pageNumber: 0, pageSize: PageSize.Small } }));
    }
  }
  

  public addHandler(args?: AddEvent): void { 
    if (args) {
      this.closeEditor(args.sender);
    }
  
    this.isInEditing = true;
  
    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'blur' 
      }),
      internalNumber: new UntypedFormControl(null, {
        validators: Validators.required, 
        asyncValidators: ProductInternalNumberValidator(this.productService),
        updateOn: 'blur' 
      })
    });
  
    if (args) {
      args.sender.addRow(this.formGroup);
    } else if (this.productGrid) {
      this.productGrid.addRow(this.formGroup);
    }
  }
  
  public editHandler(args: EditEvent): void{
    this.closeEditor(args.sender);
    this.isInEditing = true;

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(
        args.dataItem.name, {
          validators: Validators.required, 
          updateOn: 'blur' 
        }),
      internalNumber: new UntypedFormControl(
        args.dataItem.internalNumber,
        Validators.required
        )
    });

    this.editedRowIndex = args.rowIndex;

    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  public cancelHandler(args: CancelEvent): void {
    this.closeEditor(args.sender, args.rowIndex);
  }

  public saveHandler(event: SaveEvent): void {
    const createRequest: ICreateProductRequest = {
      name: event.dataItem.name,
      internalNumber: event.dataItem.internalNumber
    }
    const updateRequest: IUpdateProductRequest = {
      name: event.formGroup.value.name,
      id: event.dataItem.id,
    }
    if(event.dataItem.id == undefined){
      this.createItemSub$.next(createRequest)
    } else {
      this.updateItemSub$.next(updateRequest)
    }
    this.closeEditor(event.sender);
  }

  private closeEditor(grid : GridComponent, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup.reset();
    this.isInEditing = false;
  }  

  public removeHandler(event: RemoveEvent): void {
    this.selectedItemToDelete = event.dataItem; // Store the item
    this.deletionDialogOpened = true; // Open the confirmation dialog
  }

  toggleProductActive(product: IProduct): void {
    if (product.isActive) {
      this.selectedProductToDeactivate = product;
      this.inactiveReasonInput = '';
      this.deactivateDialogOpened = true;
    } else {
      const request: IUpdateProductRequest = {
        name: product.name,
        id: product.id,
        isActive: true
      };
      this.updateItemSub$.next(request);
    }
  }

  confirmDeactivation(): void {
    const product = this.selectedProductToDeactivate;
    const request: IUpdateProductRequest = {
      name: product.name,
      id: product.id,
      isActive: false,
      inactiveReason: this.inactiveReasonInput || null
    };
    this.updateItemSub$.next(request);
    this.deactivateDialogOpened = false;
    this.selectedProductToDeactivate = null;
  }

  cancelDeactivation(): void {
    this.deactivateDialogOpened = false;
    this.selectedProductToDeactivate = null;
    this.inactiveReasonInput = '';
  }

  // Adds a product with its quantity to
  addToCart(quantity: number, product: IProduct) {
    if(this.isAdminView){
      const request: ICreateBasketItemRequest = {
        quantity: quantity,
        productId: product.id,
        companyId: this.currentCompany.id
      }
  
      this.basketService.addBasketItem(request)
        .pipe(untilDestroyed(this))
        .subscribe(response => {
          this.reloadProducts();
          this.showAddToCartSuccess();
      });
    }else {
      const request: ICreateBasketItemRequest = {
        quantity: quantity,
        productId: product.id,
        companyId: this.loggedInCompanyId
      }
  
      this.basketService.addBasketItem(request)
        .pipe(untilDestroyed(this))
        .subscribe(response => {
          this.reloadProducts();
          this.showAddToCartSuccess();
      });
    }

  }

  showAddToCartSuccess(){
    this.appnotificationService.notification$.next({message:translate('basket.createSuccess'), style: "success"});
  }

  onSubmit(request: IBaseSearchRequest){
    const formValues = this.filterForm.value;

    if (formValues.keyword == '' || formValues.keyword == null) {
      this.onReset();
    } else {
      const searchRequest: IBaseSearchRequest = {
        ...request,
      };
  
      searchRequest.keyword = formValues.keyword.trim()
  
      this.searchRequest$.next(searchRequest);
  
      this.showReset = true
    }
  }

  onReset(){
    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})
    this.keyword = null;
    this.filterForm.reset();
    this.showReset = false;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      document.getElementById("file-upload-label").innerHTML = this.selectedFile.name;
      this.importForm.patchValue({ file: this.selectedFile });
    }
  }

  showImportForm(): void {
    this.showUpload = true;
  }

  onUpload(): void {
    if (this.importForm.invalid) return;

    const formData = new FormData();
    formData.append('csvFile', this.importForm.get('file')?.value);

    this.store.dispatch(ProductsActions.importProducts({csv: formData}));

    this.cancelUpload();
  }

  cancelUpload() {
    this.showUpload = false;
    this.importForm.reset();
  }

  // delete confirmation dialog
  public deletionDialogClose(status: string): void {
    this.deletionDialogOpened = false;

    if(status == 'delete' && this.selectedItemToDelete) {
        this.deleteItemSub$.next(this.selectedItemToDelete.id)
    }

    this.selectedItemToDelete = null;
  }
}